import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { formatDistanceToNowStrict } from 'date-fns'
import { Clock } from '@factorialco/f0-react/icons/app'
import {
  F0Box,
  F0Card,
  F0Icon,
  F0Link,
  F0TagStatus,
  F0Text,
  ProgressBarChart,
} from '@factorialco/f0-react'
import { NumberInput, Switch } from '@factorialco/f0-react/dist/experimental'
import {
  GoalsMetricValueStatusEnum,
  PerformanceGoalsMetricRowFragment,
  PerformanceGoalsUpdateQualitativeMetricDocument,
  PerformanceGoalsUpdateQuantitativeMetricDocument,
} from 'generated/resources/graphql'
import { DocumentNode } from 'graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { MetricDetailsPanel } from 'modules/goals/components/MetricDetailsPanel'
import { UpdateMetricModal } from 'modules/goals/components/UpdateMetricModal'
import { useDeleteMetric } from 'modules/goals/hooks/deleteMetric'
import { normalizeMetric } from 'modules/goals/lib/normalizeMetric'

import { getStatusConfig } from 'modules/goals/lib/goalStatus'

import { buildDropdownItems } from './utils'

const DEBOUNCE_MS = 1500

export interface KeyResultRowProps {
  metric: PerformanceGoalsMetricRowFragment
  refetchQueries: DocumentNode[]
  showAssignee: boolean
}

export const KeyResultRow: React.FC<KeyResultRowProps> = ({
  metric,
  refetchQueries,
  showAssignee,
}) => {
  const { navigation } = useNavigation()
  const { handleDelete } = useDeleteMetric()

  const normalizedMetric = useMemo(() => normalizeMetric(metric), [metric])
  const needsAttention =
    normalizedMetric.progressAssessmentStatus === 'at_risk' ||
    normalizedMetric.progressAssessmentStatus === 'highly_at_risk'
  const quantitativeValue =
    normalizedMetric.type === 'quantitative' ? normalizedMetric.quantitativeValue : null
  const status = useMemo(
    () => getStatusConfig(metric.progressAssessmentStatus),
    [metric.progressAssessmentStatus]
  )
  const deadlineLabel = useMemo(
    () => formatDistanceToNowStrict(new Date(metric.endOn), { addSuffix: true }),
    [metric.endOn]
  )

  const assigneeName = metric.assignee?.fullName
  const assigneeLink = metric.assignee?.goalsEmployee
    ? navigation.goals.employees.employee.fullPath({ id: metric.assignee.goalsEmployee.id })
    : undefined
  const canEdit = metric._policies?.update?.can
  const canDelete = metric._policies?.delete?.can

  const [editing, setEditing] = useState(false)
  const [seeDetails, setSeeDetails] = useState(false)
  const [displayValue, setDisplayValue] = useState(quantitativeValue?.currentValue ?? 0)
  const updateTimerRef = useRef<ReturnType<typeof setTimeout>>()

  const [updateQuantitativeMetric] = useMutation(PerformanceGoalsUpdateQuantitativeMetricDocument, {
    extractorFn: (d) => d?.goalsMutations?.updateQuantitativeCurrentValueGoalsMetric,
    refetchQueries,
    awaitRefetchQueries: true,
  })

  const [updateQualitativeMetric] = useMutation(PerformanceGoalsUpdateQualitativeMetricDocument, {
    extractorFn: (d) => d?.goalsMutations?.updateQualitativeCurrentStatusGoalsMetric,
    refetchQueries,
    awaitRefetchQueries: true,
  })

  useEffect(() => {
    return () => {
      if (updateTimerRef.current) clearTimeout(updateTimerRef.current)
    }
  }, [])

  const handleQuantitativeChange = useCallback(
    (value: number | null) => {
      const newValue = value ?? 0
      setDisplayValue(newValue)
      if (updateTimerRef.current) clearTimeout(updateTimerRef.current)
      updateTimerRef.current = setTimeout(() => {
        updateQuantitativeMetric({ variables: { id: metric.id, value: newValue } })
      }, DEBOUNCE_MS)
    },
    [metric.id, updateQuantitativeMetric]
  )

  const handleQualitativeChange = useCallback(
    (checked: boolean) => {
      const newStatus: GoalsMetricValueStatusEnum = checked ? 'achieved' : 'not_achieved'
      updateQualitativeMetric({ variables: { id: metric.id, status: newStatus } })
    },
    [metric.id, updateQualitativeMetric]
  )

  const dropdownItems = useMemo(
    () =>
      buildDropdownItems({
        canEdit,
        canDelete,
        onEdit: () => setEditing(true),
        onDelete: () => handleDelete(metric),
      }),
    [canEdit, canDelete, metric, handleDelete]
  )

  return (
    <>
      <F0Card
        title={metric.title}
        otherActions={dropdownItems}
        compact
        onClick={() => setSeeDetails(true)}
      >
        <F0Box display="grid" columns="12" alignItems="center" gap="xl">
          <F0Box
            display="flex"
            flexDirection="row"
            gap="sm"
            alignItems="center"
            minWidth="0"
            colSpan="4"
          >
            <F0Icon icon={Clock} color="secondary" />
            <F0Text variant="description" content={deadlineLabel} />

            {showAssignee && assigneeName && (
              <>
                <F0Text variant="description" content="•" />
                {assigneeLink ? (
                  <F0Link href={assigneeLink} variant="unstyled">
                    {assigneeName}
                  </F0Link>
                ) : (
                  <F0Text variant="description" content={assigneeName} />
                )}
              </>
            )}
          </F0Box>

          <F0Box minWidth="0" colSpan="3">
            <ProgressBarChart
              value={metric.progressPercentageValue < 100 ? metric.progressPercentageValue : 100}
              max={100}
              label={`${i18n.formatNumber(metric.progressPercentageValue)}%`}
              color={needsAttention ? 'feedback-neutral' : 'feedback-positive'}
            />
          </F0Box>

          <F0Box
            display="flex"
            flexDirection="row"
            gap="sm"
            alignItems="center"
            justifyContent="end"
            colSpan="3"
          >
            {normalizedMetric.type === 'quantitative' && quantitativeValue && (
              <F0Box display="flex" flexDirection="row" gap="sm" alignItems="center">
                <F0Box maxWidth="28">
                  <NumberInput
                    label={i18n.t('goals_revamp.detail.current_value')}
                    hideLabel
                    hint=""
                    locale={i18n.locale}
                    disabled={!metric._policies?.updateQuantitativeCurrentValue?.can}
                    min={
                      quantitativeValue.startValue < quantitativeValue.targetValue
                        ? quantitativeValue.startValue
                        : undefined
                    }
                    value={displayValue}
                    step={1}
                    maxDecimals={2}
                    onChange={handleQuantitativeChange}
                    size="sm"
                  />
                </F0Box>

                <F0Text
                  variant="description"
                  content={`/ ${i18n.formatNumber(quantitativeValue.targetValue)}`}
                />
              </F0Box>
            )}

            {normalizedMetric.type === 'qualitative' && (
              <F0Box display="flex" flexDirection="row" alignItems="center" height="8">
                <Switch
                  title={i18n.t('performance_goals.metric_row.completed')}
                  disabled={!metric._policies?.updateQualitativeCurrentStatus?.can}
                  checked={normalizedMetric.qualitativeValue === 'achieved'}
                  onCheckedChange={handleQualitativeChange}
                />
              </F0Box>
            )}
          </F0Box>

          <F0Box display="flex" justifyContent="end" colSpan="2">
            {status && <F0TagStatus text={status.label} variant={status.variant} />}
          </F0Box>
        </F0Box>
      </F0Card>

      {editing && (
        <UpdateMetricModal
          metric={normalizeMetric(metric)}
          onClose={() => setEditing(false)}
          refetchQueries={refetchQueries}
        />
      )}

      {seeDetails && (
        <MetricDetailsPanel metricId={metric.id} onClose={() => setSeeDetails(false)} />
      )}
    </>
  )
}
